// Per-frame runtime helpers: the mutable loop/sim state, the fixed-timestep
// stepping + staggered deploy, match/run end handling, and the render + HUD
// sync. Pure glue between the immutable World and the imperative renderer/DOM.

import type { CameraView, Renderer } from "~/render/gpu";
import type { Overlay } from "~/render/overlay";
import { fieldViewport } from "~/render/view";
import type { Lobby } from "~/ui/arcade-lobby";
import { recordScore, runScore } from "~/ui/highscores";
import type { Setup } from "~/ui/setup";
import type { Ui } from "~/ui/ui";
import {
  ARENA,
  BURST_EXPLOSION,
  initArcadeWorld,
  initWorld,
  type MatchConfig,
  type Msg,
  type RunState,
  setOrbitPhase,
  type World,
} from "~/world";
import { augmentTier } from "~/world/augments";
import { updateGridDimensions } from "./input";

// Staggered launch muster: a new match starts with an empty arena and deploys
// its fleet one ship at a time, this many seconds apart, from the bases.
export const DEPLOY_INTERVAL_S = 0.17;

// The mutable runtime state shared between the UI handlers and the render loop:
// the current immutable world plus the two live-tunable rates. A starter swaps
// the whole world; ticks swap it each frame via `dispatch`.
export interface Sim {
  world: World;
  simRate: number;
  reinforceRate: number;
}

// Mutable state carried between successive loop frames, threaded through the
// per-frame helpers below.
export interface LoopState {
  gen: number;
  resetScheduled: boolean;
  shake: number;
  lastBoomId: number;
  prevAge: number;
  deployRemaining: number; // launch-fleet ships still to muster in
  deployTimer: number; // seconds until the next muster spawn
  // The flip banner this loop put on screen ("" = the banner is someone
  // else's). Kept so the beat only ever clears a line it wrote itself and can
  // never wipe a win/game-over banner out from under its timeout.
  flipBanner: string;
}

export const initLoopState = (): LoopState => ({
  gen: 0,
  resetScheduled: false,
  shake: 0,
  lastBoomId: 0,
  prevAge: 0,
  deployRemaining: 0,
  deployTimer: 0,
  flipBanner: "",
});

// Advance the fixed-timestep sim by however many ticks have accumulated, and
// fire the periodic reinforcement spawn.
export const stepSimulation = (
  advanceSim: (dt: number) => number,
  dispatch: (msg: Msg) => void,
  dt: number,
  now: number,
  reinforceRate: number,
  state: LoopState,
) => {
  const steps = advanceSim(dt);
  if (steps > 0) {
    dispatch({ kind: "tick", steps, now });
    state.gen += steps;
    if (reinforceRate > 0) {
      const spawnInterval = Math.floor(600 / reinforceRate);
      if (state.gen % spawnInterval < steps) dispatch({ kind: "replenish" });
    }
  }
};

// Carry the HUD's control-model toggle into the sim (#29). The signal is the
// player-facing source of truth, but the model has to live on the World to stay
// deterministic, so this reconciles the two each frame — which also survives a
// reset or a fresh match, where the new world starts back on inertial.
export const syncControlModel = (
  dispatch: (msg: Msg) => void,
  world: World,
  direct: boolean,
) => {
  const model = direct ? "direct" : "inertial";
  if (world.controlModel !== model) dispatch({ kind: "controlModel", model });
};

// Deploy the launch fleet a ship at a time — each `replenish` mans the emptiest
// live base, so a fresh match musters in staggered from the bases instead of
// the whole fleet popping in at once.
export const stepDeploy = (
  dispatch: (msg: Msg) => void,
  dt: number,
  state: LoopState,
) => {
  if (state.deployRemaining <= 0) return;
  state.deployTimer -= dt;
  if (state.deployTimer > 0) return;
  dispatch({ kind: "replenish" });
  state.deployRemaining -= 1;
  state.deployTimer = DEPLOY_INTERVAL_S;
};

// Match end: show the winner banner once, then reopen the setup screen so the
// player can tweak and launch a fresh match. `resetScheduled` latches until the
// next starter clears it, so this fires exactly once per match.
export const handleMatchEnd = (
  world: World,
  ui: Ui,
  state: LoopState,
  setup: Setup,
) => {
  if (world.winner && !state.resetScheduled) {
    state.resetScheduled = true;
    ui.banner.val =
      world.winner === "draw" ? "DRAW" : `${world.winner.toUpperCase()} WINS`;
    setTimeout(() => {
      ui.banner.val = "";
      setup.show();
    }, 3600);
  }
};

// Arcade run end: the pilot burned their last life. Show a game-over line once,
// then reopen the lobby to pick a hull and try again. Mirrors handleMatchEnd's
// latch so it fires exactly once per run.
export const handleArcadeEnd = (
  world: World,
  ui: Ui,
  state: LoopState,
  lobby: Lobby,
) => {
  const a = world.run;
  if (a?.over && !state.resetScheduled) {
    state.resetScheduled = true;
    // Bank the run before the banner so a new personal best can be called out;
    // the lobby that reopens below reads the same table.
    const result = runScore(world);
    const rank = result ? recordScore(result.difficulty, result.entry) : null;
    ui.banner.val =
      `GAME OVER${a.waves ? ` · wave ${a.waves.wave}` : ""} · ${a.kills} kills` +
      (result ? ` · ${result.entry.score} pts` : "") +
      (rank === 0 ? " · NEW BEST" : "");
    setTimeout(() => {
      ui.banner.val = "";
      lobby.show();
    }, 3600);
  }
};

// How far into the fight the beat's line stays up, in generations. It announces
// the gear change; once the ambush is being shot at, it is in the way.
const FLIP_BANNER_HOLD_GENS = 90;

/** The line the flip beat wants on screen this frame ("" = none). */
const flipBannerText = (world: World): string => {
  const flip = world.flip;
  if (!flip || world.run?.over) return "";
  // Up for the whole brake, so the line lands with the stop rather than after
  // it, then held briefly into act two.
  const showing =
    flip.phase === "halt" ||
    (flip.phase === "fight" && flip.gens < FLIP_BANNER_HOLD_GENS);
  return showing ? flip.banner : "";
};

// The flip beat's announcement (#31): the banner half of the punctuation the
// hard cut shipped without. Driven off the live world rather than a timeout,
// so it follows the beat if the brake is interrupted or the run ends under it.
export const handleFlipBanner = (world: World, ui: Ui, state: LoopState) => {
  const text = flipBannerText(world);
  if (text === state.flipBanner) return;
  // Only ever write over our own line: a win/game-over banner owns the slot
  // while its timeout runs, and this must not step on it.
  if (text || ui.banner.val === state.flipBanner) ui.banner.val = text;
  state.flipBanner = text;
};

// Build this frame's instance buffers from the World and hand them to the GPU
// renderer.
export const buildAndRender = (
  overlay: Overlay,
  renderer: Renderer,
  canvas: HTMLCanvasElement,
  world: World,
  now: number,
  showHp: boolean,
  camera: CameraView,
  detail: number,
) => {
  // Align the furniture ring to the world being drawn (the sim advances age
  // between renders; this keeps rendered bases/portals/pads on the live orbit).
  setOrbitPhase(world.age);
  // Where the field lands in the window, and how big a cell is there.
  const vp = fieldViewport(canvas.width, canvas.height, ARENA.w, ARENA.h);
  // Follow the stage: the field origin is where the window sits, so the camera
  // is that origin in pixels, less the gutter that centres the field. Zero for
  // all-range play, where the origin is 0,0 and the field fills the window.
  renderer.setCamera(
    ARENA.x0 * vp.scale - vp.offsetX,
    ARENA.y0 * vp.scale - vp.offsetY,
  );
  const frame = overlay.build({
    cellPx: vp.scale,
    now,
    world,
    showHp,
    detail,
  });
  renderer.render(frame, now / 1000, camera);
};

// Screen shake: each fresh explosion punches the canvas; it decays fast.
// Burst ids are monotonic per match; a reset (age rewinds) clears the mark.
export const updateScreenShake = (
  canvas: HTMLCanvasElement,
  world: World,
  now: number,
  state: LoopState,
  /** Graphics-tier shake multiplier; 0 under prefers-reduced-motion. */
  scale = 1,
) => {
  if (world.age < state.prevAge) state.lastBoomId = 0;
  state.prevAge = world.age;
  for (const b of world.bursts.items) {
    if (b.kind === BURST_EXPLOSION && b.id > state.lastBoomId) {
      state.lastBoomId = b.id;
      state.shake = Math.min(7, state.shake + 2.2);
    }
  }
  state.shake *= 0.86;
  // The decay still runs at full strength so the scale is a pure presentation
  // knob — turning it back up mid-explosion doesn't resurrect an old punch.
  const amp = state.shake * scale;
  canvas.style.transform =
    amp > 0.15
      ? `translate(${Math.sin(now * 0.11) * amp}px, ${Math.cos(now * 0.17) * amp}px)`
      : "";
};

// The HUD status/phase line for the current world (arcade wave/lives, autobattle
// reinforce/sudden-death, or endless).
const getRunPhaseText = (world: World, a: RunState): string => {
  if (a.over) return "game over";
  const tier = augmentTier(a.augments);
  const mk = tier > 0 ? ` · Mk ${tier}` : ""; // prestige readout past L5
  const w = a.waves;
  if (w) return `wave ${w.wave} · ${w.waveRemaining + w.pending} enemies${mk}`;
  // No wave director (a scroll stage) → the wave counter has nothing to say,
  // and with no scoreboard beside it the run's own points belong here.
  const points = world.score[world.config.run?.playerTeam ?? ""] ?? 0;
  return `${points} pts · ${a.kills} kills${mk}`;
};

const getHudPhaseText = (world: World): string => {
  if (world.run) return getRunPhaseText(world, world.run);
  if (world.winner) return "match over";
  if (world.config.format === "endless") return "endless";
  const remain = world.config.reinforceGens - world.age;
  return remain > 0
    ? `reinforcements ${Math.ceil(remain / world.config.tempo)}s`
    : "no reinforcements";
};

// Push per-frame world state into the reactive HUD handles.
export const updateHud = (ui: Ui, world: World) => {
  ui.score.val = world.score;
  const counts: Record<string, number> = {};
  for (const s of world.ships.items) {
    counts[s.colorName] = (counts[s.colorName] ?? 0) + 1;
  }
  ui.counts.val = counts;
  const phase = getHudPhaseText(world);
  const rallyText = world.rally ? ` · ${world.rally.team} rally point` : "";
  ui.status.val =
    `ships ${world.ships.items.length} · ${phase}` +
    ` · HP bars: ${ui.hpOn.val ? "on (h)" : "off (h)"}` +
    ` · stick: ${world.controlModel} (i)` +
    rallyText;
  ui.controlledShip.val =
    world.controlledShipId !== null
      ? world.ships.items.find((s) => s.id === world.controlledShipId) || null
      : null;
  ui.arcadeLives.val = world.run && !world.run.over ? world.run.lives : null;
};

// Track the canvas backing size; when the client box changes, re-derive the grid
// bounds and resize the renderer. Owns its own last-size state so the loop stays
// a one-line call.
export const createResizeSync = (
  renderer: Renderer,
  canvas: HTMLCanvasElement,
) => {
  let lastW = 0;
  let lastH = 0;
  return () => {
    if (canvas.clientWidth === lastW && canvas.clientHeight === lastH) return;
    lastW = canvas.clientWidth;
    lastH = canvas.clientHeight;
    updateGridDimensions(canvas);
    renderer.resize();
  };
};

// The two match launchers, sharing the runtime's mutable Sim + loop state. Both
// swap in a fresh world and reset the per-match rates; autobattle musters its
// fleet in staggered (stepDeploy), arcade spawns the controlled pilot outright.
export const createStarters = (sim: Sim, ui: Ui, loopState: LoopState) => {
  const reset = (cfg: MatchConfig) => {
    sim.simRate = cfg.tempo;
    ui.activeTeamCount.val = cfg.teams;
    ui.banner.val = "";
    loopState.resetScheduled = false;
  };
  const startMatch = (cfg: MatchConfig) => {
    sim.world = initWorld(Date.now(), { ...cfg, initialShips: 0 });
    sim.reinforceRate = cfg.reinforceRate;
    reset(cfg);
    ui.hudTitle.val = "Autobattle";
    ui.setSimKnobsHidden(false);
    ui.setScoreHidden(false);
    loopState.deployRemaining = cfg.initialShips;
    loopState.deployTimer = DEPLOY_INTERVAL_S;
  };
  const startArcadeMatch = (cfg: MatchConfig) => {
    sim.world = initArcadeWorld(Date.now(), cfg);
    sim.reinforceRate = 0;
    reset(cfg);
    // Arcade: the stage/wave sets tempo + spawns, so hide the sim knobs.
    const diff = cfg.run?.difficulty ?? "normal";
    ui.hudTitle.val = `Arcade · ${diff[0].toUpperCase()}${diff.slice(1)}`;
    ui.setSimKnobsHidden(true);
    // A stage is flown against a script, not against rival teams: the board
    // would rank four colours where only two are ever on the field.
    ui.setScoreHidden(cfg.format === "scroll");
    loopState.deployRemaining = 0;
    loopState.deployTimer = 0;
  };
  return { startMatch, startArcadeMatch };
};
